import React, { useState, useEffect } from 'react';
import { Button, Modal, message, Switch } from 'antd';
import styled from 'styled-components';
import InnerImageZoom from 'react-inner-image-zoom';

import withAuth from '~/libs/withAuth';
import InvoiceEditor from '~/components/InvoiceEditor';
import { updateInvoice } from '~/libs/requests';
import { jsonFetcher, swrInvalidateMany } from '~/libs/helpers';
import { Accounts } from '~/libs/constants';

const StyledInnerImageZoom = styled(InnerImageZoom)`
	width: 100%;
	box-shadow: 0px 2px 5px rgba(0, 0, 0, 0.35);
`;

const ModalContent = styled.div`
	display: flex;
`;

const ModalContentMedia = styled.div`
  max-width: 380px;
`;

const ModalContentForm = styled.div`
	flex: 2;
	margin-left: 20px;

	.ant-form-item-label {
    text-transform: uppercase;
    padding-bottom: 5px !important;
	}

	.ant-form-item-label label {
		color: gray;
		font-family: Lato;
		font-weight: 600;
	}
`;

const ModalTitleContainer = styled.div`
  display: flex;
  justify-content: space-between;
  padding-right: 20px;
  padding-bottom: 20px;
  margin-bottom: 35px;
  border-bottom: 1px solid #e8e8e8;

  small {
    font-weight: normal;
  }
`;

const isPayment = (entry) => {

  // isWithheldTaxEntry
  if (entry.debitAccount === Accounts.ASSETS.ADVANCE_TO && entry.creditAccount === Accounts.PROFIT.SALE)
    return false;
  
    // isInitialReceivableEntry
  if (entry.debitAccount === Accounts.ASSETS.ACC_REC && entry.creditAccount === Accounts.PROFIT.SALE)
    return false;

  // Payment
  if (entry.creditAccount === Accounts.PROFIT.SALE || entry.creditAccount === Accounts.ASSETS.ACC_REC) {
    return true;
  }

  return false;
}

const InvoiceModal = (props) => {
	const [ busy, setBusy ] = useState(false);
  const [ visible, setVisible ] = useState();
  const [ editInvoice, setEditInvoice ] = useState();
  const [ isExpert, setIsExpert ] = useState();
  const { invoice, onClose } = props;

  useEffect(() => {
    if (invoice) {
      setBusy(true);
      loadJournalEntries();
      setVisible(true);
    }
    else {
      setEditInvoice(null);
      setVisible(false);
    }
  }, [invoice]);

  const onIsExpertChange = (isExpert) => {
    setEditInvoice({ ...invoice, isExpert });
  }

  const loadJournalEntries = async () => {
    const journalEntries = await jsonFetcher(`/api/journal/${invoice._id}`);
    let freshPayments = journalEntries.data.filter(x => isPayment(x, journalEntries));
    freshPayments = freshPayments.map(x => ({ 
      _id: x._id,
      date: x.date,
      year: x.year,
      month: x.month,
      day: x.day,
      debitSubAccountId: x.debitSubAccountId,
      amount: x.debitAmount,
      })).sort((a, b) => a.date - b.date);
    const freshInvoice = { ...invoice, payments: freshPayments };
    setEditInvoice(freshInvoice);
    setBusy(false);
  }

	const onInvoiceUpdate = async (invoice) => {
		setEditInvoice(invoice);
	}

	const onSaveClick = async () => {
		setBusy(true);
		const res = await updateInvoice(editInvoice);
		if (!res.success) {
			alert(res.message);
    }
    // TODO: This is a hack. We should be able to invalidate the cache for a specific invoice.
    // And also use the official functions.
		swrInvalidateMany('/api/invoice');
    message.info("Updated an invoice.");
    onClose();
		setBusy(false);
	}

	const onCancelClick = () => {
    onClose();
	}

	return (
    <Modal open={visible} width="90%" onOk={onSaveClick} onCancel={onCancelClick}
      title={<ModalTitleContainer>
        <div>
          <span>Edit Invoice</span>
          <Switch style={{ marginLeft: 10 }} checkedChildren="Advanced" unCheckedChildren="Simple"
            checked={editInvoice?.isExpert} onChange={() => { onIsExpertChange(!editInvoice.isExpert) }} />
        </div>

        <div>
          <small>Withholding Tax:</small>
          <Switch style={{ width: 60, marginLeft: 10 }} checkedChildren="Yes" unCheckedChildren="No"
            disabled={!editInvoice || editInvoice.isPaid}
            checked={editInvoice?.withheldTax > 0}
            onChange={() => { 
              onInvoiceUpdate({...editInvoice, 
                withheldTax: editInvoice.withheldTax ? 0 : (editInvoice.amount >= 1000000 ? 20.42 : 10.21)
              }); 
            }} />
          {editInvoice?.isExpert && <>
            <small style={{ marginLeft: 10 }}>JCT Tax (10%):</small>
            <Switch style={{ width: 60, marginLeft: 10 }} checkedChildren="Yes" unCheckedChildren="No"
              disabled={!editInvoice || editInvoice.isPaid}
              checked={editInvoice?.jctTax > 0}
              onChange={() => { 
                onInvoiceUpdate({...editInvoice, jctTax: editInvoice.jctTax ? 0 : 10 }); 
              }} />
            <small style={{ marginLeft: 10 }}>Paid:</small>
            <Switch style={{ marginLeft: 10, width: 60, marginRight: 10 }} disabled={!editInvoice || !editInvoice.amount}
              checkedChildren="Yes" unCheckedChildren="No" checked={editInvoice?.isPaid} 
              onChange={(checked) => { onInvoiceUpdate({...editInvoice, isPaid: checked }); }} />
          </>}
        </div>
      </ModalTitleContainer>}
      footer={[
        <Button key="back" onClick={onCancelClick}>Cancel</Button>,
        <Button key="submit" type="primary" loading={busy} onClick={onSaveClick}>Save Invoice</Button>,
      ]}> 
      <ModalContent>

        <ModalContentMedia>
          {invoice && invoice.media && invoice.media.thumbnails &&
            <StyledInnerImageZoom moveType='drag'
              src={invoice.media.thumbnails.large} 
              zoomSrc={invoice.media.thumbnails.large}
            />
          }
        </ModalContentMedia>
      
        <ModalContentForm>
          {editInvoice && <InvoiceEditor invoice={editInvoice} onInvoiceUpdate={onInvoiceUpdate} isExpert={isExpert} />}
        </ModalContentForm>

      </ModalContent>
    </Modal>
	);

};

export default withAuth(InvoiceModal);